"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import AppLayoutWithSidebar from "./AppLayoutWithSidebar";

/**
 * Root-level iframe gate. Swaps between the full FrenFlow chrome
 * (AppLayoutWithSidebar) and bare content depending on where we're
 * mounted. Kept in a separate component — not inside
 * AppLayoutWithSidebar — so that when the iframe-detection effect
 * flips the state, React unmounts the whole chrome tree cleanly
 * instead of tripping the Rules of Hooks with a partial render.
 *
 * Two signals both force "bare mode":
 *   - pathname under /embed/* (direct same-site embeds)
 *   - running inside an iframe whose parent we can't read
 *     (Discord Activity rewrites window.location to its own domain,
 *     so a Discord-hosted embed may not look like /embed/ to Next.js
 *     but we still don't want to leak our sidebar into Discord).
 */
export default function IframeGate({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const [isInIframe, setIsInIframe] = useState(false);

  useEffect(() => {
    try {
      setIsInIframe(typeof window !== "undefined" && window.self !== window.top);
    } catch {
      setIsInIframe(true);
    }
  }, []);

  if (pathname?.startsWith("/embed") || isInIframe) {
    return <>{children}</>;
  }

  return <AppLayoutWithSidebar>{children}</AppLayoutWithSidebar>;
}
